import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from, map, of } from 'rxjs';
import { VendorOperatingStatesForm } from 'src/app/classes/form-groups/vendor-operating-states-form';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { OperatingStateOption } from 'src/app/interfaces/operating-state-option';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { OperatingStatesModalComponent } from 'src/app/modals/operating-states-modal/operating-states-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { VendorTradeStateService } from 'src/app/services/vendor-trade-state.service';

// The roles the vendor PATCH accepts (ADMIN_CS_PM). Other admin teams get a
// read-only view until the backend widens it.
const EDIT_ROLES = new Set<UserAccountRoles>([
  'ADMIN_ADMIN',
  'ADMIN_CUSTOMER_SERVICE_TEAM',
  'ADMIN_PROJECT_MANAGEMENT_TEAM'
]);

@Component({
  selector: 'app-vendor-operating-states',
  templateUrl: './vendor-operating-states.component.html',
  styleUrl: './vendor-operating-states.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorOperatingStatesComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);
  constructor(
    private AuthService: AuthService,
    private ModalService: NgbModal,
    private SubNationalDivisionService: SubNationalDivisionService,
    private ToastService: ToastService,
    private VendorTradeStateService: VendorTradeStateService
  ) {}

  @Input() VendorId!: number;
  @Input() country!: 'US' | 'CA';
  @Input() localizationInfo!: LocalizationInfo;
  @Input() OperatingStatesForm!: VendorOperatingStatesForm;

  isLoaded = false;
  canEdit = false;

  // SubNationalDivisionId -> legal names of linked clients that operate there.
  private clientNamesByDivisionId = new Map<number, string[]>();
  private countryOptions?: OperatingStateOption[];

  get singular(): string {
    return this.localizationInfo.territory.label.toLowerCase();
  }

  get plural(): string {
    return this.localizationInfo.territoryPlural.label.toLowerCase();
  }

  get selected(): OperatingStateOption[] {
    return this.OperatingStatesForm.controls.SubNationalDivisions.value;
  }

  getRequirementTitle(state: OperatingStateOption): string | null {
    return state.clientNames.length === 0
      ? null
      : `Drives Trade License requirements for ${state.clientNames.join(', ')}`;
  }

  ngOnInit(): void {
    const { UserAccountRole: { role } = {} } = this.AuthService.getUser();
    this.canEdit = !!role && EDIT_ROLES.has(role);

    this.VendorTradeStateService.getOperatingStates(this.VendorId).subscribe({
      next: ({ operatingStates, clientNamesByDivisionId }) => {
        this.clientNamesByDivisionId = clientNamesByDivisionId;
        this.VendorTradeStateService.setSavedOperatingStates(
          this.OperatingStatesForm,
          operatingStates
        );
        this.isLoaded = true;
        this._cdr.markForCheck();
      },
      error: () => {
        this.ToastService.addErrorToast(
          'Error',
          `Could not load operating ${this.plural}.`
        );
      }
    });
  }

  removeState(state: OperatingStateOption): void {
    const removeSelection = (): void => {
      this.setSelected(this.selected.filter(({ id }) => id !== state.id));
    };

    if (this.selected.length > 1) {
      removeSelection();
      return;
    }

    this.confirmNoStates(
      `Remove the last operating ${this.singular}?`,
      `Remove ${this.singular}`,
      removeSelection
    );
  }

  chooseStates(): void {
    this.getCountryOptions().subscribe({
      next: (options) => {
        const { componentInstance, result } = this.ModalService.open(
          OperatingStatesModalComponent,
          { backdrop: 'static', size: 'lg', scrollable: true }
        );

        Object.assign(componentInstance, {
          options,
          localizationInfo: this.localizationInfo,
          selected: this.selected
        });

        from(result).subscribe({
          next: (selected: OperatingStateOption[] | null) => {
            if (!selected) {
              return;
            }

            if (selected.length === 0 && this.selected.length > 0) {
              this.confirmNoStates(
                `Remove every operating ${this.singular}?`,
                'Remove all',
                () => this.setSelected([])
              );
              return;
            }

            this.setSelected(selected);
          },
          // Esc dismisses the modal, which rejects `result`: nothing to do.
          error: () => undefined
        });
      },
      error: () => {
        this.ToastService.addErrorToast(
          'Error',
          `Could not load ${this.plural}.`
        );
      }
    });
  }

  private confirmNoStates(
    title: string,
    continueButtonText: string,
    onConfirm: () => void
  ): void {
    const { componentInstance, closed } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', modalDialogClass: 'confirm-modal' }
    );

    Object.assign(componentInstance, {
      title,
      displayText: `This vendor will have no operating ${this.plural}, so no Trade Licenses will be required for any client. You can add them back at any time.`,
      continueButtonText,
      confirmTone: 'danger'
    });

    closed.subscribe((proceed: boolean) => {
      if (proceed) {
        onConfirm();
      }
    });
  }

  private setSelected(selected: OperatingStateOption[]): void {
    const sorted = [...selected].sort((left, right) =>
      left.name.localeCompare(right.name)
    );
    const control = this.OperatingStatesForm.controls.SubNationalDivisions;

    control.setValue(sorted);
    control.markAsDirty();
    this._cdr.markForCheck();
  }

  private getCountryOptions(): Observable<OperatingStateOption[]> {
    if (this.countryOptions) {
      return of(this.countryOptions);
    }

    return this.SubNationalDivisionService.getSubNationalDivisions({
      country: this.country
    }).pipe(
      map((divisions: SubNationalDivision[]) => {
        const options: OperatingStateOption[] = [];

        divisions.forEach(({ id, name, abbreviation }) => {
          if (id && name && abbreviation) {
            options.push({
              id,
              name,
              abbreviation,
              clientNames: this.clientNamesByDivisionId.get(id) ?? []
            });
          }
        });

        this.countryOptions = options.sort((left, right) =>
          left.name.localeCompare(right.name)
        );

        return this.countryOptions;
      })
    );
  }
}
